
import React from 'react';
import { motion } from 'framer-motion';
import CartSellerGroup from './CartSellerGroup';
import OrderSummary from './OrderSummary';
import { CartItem } from '../../types';
import OfflineIndicator from './OfflineIndicator';

interface CartLayoutWebProps {
    sellers: Record<string, CartItem[]>;
    totalPrice: number;
    totalItems: number;
    onRemove: (listingId: string) => void;
    onCheckout: (sellerId: string | null, sellerName: string, amount: number) => void;
    onClearCart: () => void;
}

const CartLayoutWeb: React.FC<CartLayoutWebProps> = ({
    sellers,
    totalPrice,
    totalItems,
    onRemove,
    onCheckout,
    onClearCart
}) => {
    return (
        <div className="max-w-7xl mx-auto px-6 py-12">
            <OfflineIndicator />
            <h1 className="text-4xl font-black text-gray-950 dark:text-white mb-10 flex items-center justify-between uppercase tracking-tighter italic">
                <span>Sepetim <span className="text-blue-500 not-italic tracking-normal ml-1">({totalItems})</span></span>
                <button
                    onClick={onClearCart}
                    className="text-[10px] font-black text-gray-400 dark:text-gray-500 hover:text-rose-500 dark:hover:text-rose-400 transition-all uppercase tracking-widest bg-gray-100 dark:bg-gray-900 px-4 py-2 rounded-xl"
                >
                    Sepeti Temizle
                </button>
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                {/* Left Column: Items (Merged List or Grouped) */}
                <div className="lg:col-span-8 space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="space-y-6"
                    >
                        {Object.keys(sellers).map(sellerId => (
                            <CartSellerGroup
                                key={sellerId}
                                sellerId={sellerId}
                                items={sellers[sellerId]}
                                onRemove={onRemove}
                                onCheckout={Object.keys(sellers).length > 1 ? onCheckout : undefined}
                            />
                        ))}
                    </motion.div>
                </div>

                {/* Right Column: Sticky Summary */}
                <div className="lg:col-span-4">
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <OrderSummary
                            subtotal={totalPrice}
                            itemCount={totalItems}
                            onCheckout={() => {
                                // Default checkout (All or First)
                                // In multi-seller, we might force user to choose per seller, 
                                // OR if we implement 'Bundled Checkout' later. 
                                // For Phase 15C, we have per-seller checkout logic if > 1 seller.

                                if (Object.keys(sellers).length === 1) {
                                    const sid = Object.keys(sellers)[0];
                                    const sName = sellers[sid][0].sellerName || 'Satıcı';
                                    onCheckout(sid, sName, totalPrice);
                                } else {
                                    // Scroll to top or show toast "Please checkout per seller"
                                    // Or trigger global checkout modal if backend supports splitting?
                                    // Current logic: Per Seller.
                                    // We'll disable the global button if multiple sellers, handled in OrderSummary?
                                    // Let's pass a generic handler.
                                    onCheckout(null, '', totalPrice);
                                }
                            }}
                            isSticky={true}
                        />

                        {/* Info Alert */}
                        {Object.keys(sellers).length > 1 && (
                            <div className="mt-6 p-6 bg-blue-500/5 dark:bg-blue-500/10 rounded-3xl border border-blue-500/20 flex gap-4">
                                <span className="text-2xl">ℹ️</span>
                                <div className="flex-1">
                                    <p className="text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest mb-1">Bilgilendirme</p>
                                    <p className="text-blue-700 dark:text-blue-300 text-xs font-medium leading-relaxed">
                                        Birden fazla satıcıdan ürün eklediniz. Ödemenizi her satıcı için ayrı ayrı veya tek seferde yapabilirsiniz.
                                    </p>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default CartLayoutWeb;
